import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  GenericField,
  InfoPanel,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field } from 'formik';
import {
  UpdateIdpUserStoreDocument,
  type UpdateIdpUserStoreMutation,
  type UpdateIdpUserStoreMutationVariables,
} from 'generated/axauth-service';
import { type BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getManagedServiceData } from 'util/managedServiceUtil';
import { validateUrl } from 'util/validationUtil';
import * as Yup from 'yup';
import { ensureError } from '../../../../../common/errors/ensure-error';
import {
  type AuthServiceDetails,
  type SubmitResponse,
  type WebHookFormData,
} from './MigrationWebHook.types';

const webhookValidation = Yup.object<ObjectSchemaDefinition>({
  migrationWebhook: Yup.string()
    .required('Migration webhook URL is required')
    .test(
      'migration-webhook-url',
      'Migration webhook must be a valid URL',
      validateUrl,
    ),
});

export const MigrationWebHookCreate: React.FC = () => {
  const { userStoreId, envId } = useParams<{
    userStoreId: string;
    envId: string;
  }>();
  const [authServiceConfig, setAuthServiceConfig] =
    useState<AuthServiceDetails>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const history = useHistory();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const serviceData = await getManagedServiceData(
          envId,
          ManagedServiceId.AXAUTH_SERVICE,
        );
        setAuthServiceConfig(serviceData);
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, userStoreId]);

  const saveData = useCallback(
    async (formData: WebHookFormData): Promise<SubmitResponse> => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );
      const response = await envApolloClient.mutate<
        UpdateIdpUserStoreMutation,
        UpdateIdpUserStoreMutationVariables
      >({
        mutation: UpdateIdpUserStoreDocument,
        variables: {
          input: {
            id: userStoreId,
            patch: { migrationWebhook: formData.migrationWebhook },
          },
        },
      });
      return response.data;
    },
    [envId, userStoreId],
  );

  const onProceed = useCallback<ActionHandler<WebHookFormData, SubmitResponse>>(
    ({ submitResponse }) => {
      if (submitResponse) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/migration/details/secret/migration`,
        );
      }
    },
    [history, envId, userStoreId],
  );

  const Panel: React.FC = () => {
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Tenant ID">{authServiceConfig?.tenantId}</Paragraph>
          <Paragraph title="Environment ID">
            {authServiceConfig?.environmentId}
          </Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    return (
      <>
        <Field
          name="migrationWebhook"
          label="Migration Webhook URL"
          tooltipContent="A webhook that verifies an end-user's credentials against a legacy identity provider during a lazy credential migration. It is called when the user's password has not yet been re-homed into AxAuth (i.e. no matching user exists yet, or the user was provisioned via session transfer). On success, AxAuth stores the returned credentials and completes sign-in."
          as={SingleLineTextField}
        />
        <GenericField name="secret" label="Migration Webhook Secret">
          <p>Not Generated</p>
        </GenericField>
      </>
    );
  };

  return (
    <>
      <Create<WebHookFormData, SubmitResponse>
        title="Migration Webhook"
        subtitle="Create Migration Webhook"
        validationSchema={webhookValidation}
        initialData={{
          loading,
          error,
        }}
        saveData={saveData}
        infoPanel={<Panel />}
        onProceed={onProceed}
      >
        <Form />
      </Create>
    </>
  );
};

export const MigrationWebHookCreateBreadCrumb: BreadcrumbResolver = () =>
  'New Migration Webhook';
